import { useQuery } from '@tanstack/react-query';
import { fetchApi } from './client';

// ---------- Types (backend contract) ----------

/**
 * `none` = the client has no task in that department at all, i.e. they do not buy that service
 * from us. It is deliberately NOT `ok`: a green "on track" chip for a client whose sales tax we
 * do not do is a claim we cannot support. Source of truth: `FilingStatus.Status` in
 * `api/Models/BookkeepingDashboard.cs`.
 */
export type FilingStatusValue = 'none' | 'ok' | 'due_soon' | 'overdue';

export interface FilingStatus {
  status: FilingStatusValue;
  nextDueAt?: string; // ISO date string
}

export interface BookkeepingClientRow {
  companyId: string;
  companyName: string;
  bookkeeperStaffId: string;
  bookkeeperName: string;
  avgMonthlyTransactions?: number;
  currentMonthProgressPercent: number;
  openTasksCount: number;
  overdueTasksCount: number;
  /**
   * One entry per department the chip is named after. There was a `payroll` chip until
   * 2026-07-28; `cr8f7_department` has no Payroll value, so it named a department that does not
   * exist and rendered an unrelated derived state. Removed rather than left blank.
   */
  filingStatuses: {
    salesTax: FilingStatus;
    form1099: FilingStatus;
  };
  nextMeetingAt?: string;
  priorityScore?: number;
}

export interface BookkeepingClientDetail extends BookkeepingClientRow {
  recentActivity: Array<{
    type: string;
    occurredAt: string;
    summary: string;
  }>;
}

export interface BookkeepingDashboardSummary {
  totalClients: number;
  clientsByStatus: {
    current: number;
    behind: number;
    overdue: number;
  };
  overdueCount: number;
  currentMonthProgressPercent: number;
  upcomingFilingsCount: number;
}

/**
 * The six values the monthly-progress endpoint emits.
 *
 * Source of truth: `api/Models/MonthlyProgressWeekCell.cs` → `MonthlyProgressCellStatus`, resolved
 * by `BookkeepingDashboardFunctions.ClassifyWeekCell` in this precedence:
 *
 *   total == 0        → `no_tasks`     nothing was due in this week at all
 *   overdue > 0       → `overdue`      an open task is past its due date
 *   completed == total→ `closed`       everything due this week is done
 *   dueSoon > 0       → `due_soon`     nothing late, but something falls due within 3 days
 *   completed == 0    → `not_started`  work IS due here and none of it has been touched
 *   otherwise         → `in_progress`  partly done, nothing urgent
 *
 * `no_tasks` and `due_soon` were added by the 2026-07-25 backend fix. Before it, an empty week and
 * an untouched-but-scheduled week BOTH came back as `not_started` and rendered identically — the
 * single biggest reason this grid could not be read. Do not collapse them again.
 *
 * Wire values outside this union are folded into the nearest real state by
 * `normalizeWeekCellStatus` in `monthlyProgressHelpers` (and, when `weekDetails` is present,
 * re-derived from the counts), so a future backend addition degrades instead of crashing.
 */
export type WeekCellStatus =
  | 'no_tasks'
  | 'not_started'
  | 'in_progress'
  | 'due_soon'
  | 'closed'
  | 'overdue';

/**
 * The counts behind one week cell's colour.
 *
 * Backend guarantees, pinned by `api/Tests/BookkeepingDashboardFunctionsTests.cs`:
 * `weekDetails` is always length 4, `weekDetails[i].status === weekCells[i]`,
 * `completed + open <= total`, and `overdue + dueSoon <= open`. Cancelled tasks (statecode 2)
 * count toward `total` but toward neither `open` nor `completed`, so `total - completed - open`
 * is the cancelled count.
 */
export interface MonthlyProgressWeekCell {
  /** A `WeekCellStatus`; typed as `string` because it is a wire value. */
  status: string;
  total: number;
  completed: number;
  open: number;
  overdue: number;
  dueSoon: number;
}

export interface MonthlyProgressClient {
  companyId: string;
  companyName: string;
  /** Length 4 — weeks 1..4. Typed as `string[]` on the wire; normalise before use. */
  weekCells: string[];
  /**
   * Length 4, same weeks as `weekCells`, with the counts behind each status. Optional on this type
   * only so a response from a backend older than the 2026-07-25 fix still parses; the live backend
   * always sends it.
   */
  weekDetails?: MonthlyProgressWeekCell[];
}

export interface MonthlyProgressResponse {
  month: string; // YYYY-MM
  clients: MonthlyProgressClient[];
}

export interface Workload {
  bookkeeperId: string;
  bookkeeperName: string;
  totalClients: number;
  totalOpenTasks: number;
  priorityWeightedWorkload: number;
}

// ---------- Filter types ----------

export interface BookkeepingClientFilters {
  bookkeeperId?: string;
  status?: 'current' | 'behind' | 'overdue';
}

// ---------- API calls ----------

async function getBookkeepingDashboard(): Promise<BookkeepingDashboardSummary> {
  return fetchApi<BookkeepingDashboardSummary>('/bookkeeping/dashboard');
}

async function getBookkeepingClients(
  filters?: BookkeepingClientFilters
): Promise<BookkeepingClientRow[]> {
  const params = new URLSearchParams();
  if (filters?.bookkeeperId) params.set('bookkeeperId', filters.bookkeeperId);
  if (filters?.status) params.set('status', filters.status);
  const query = params.toString();
  return fetchApi<BookkeepingClientRow[]>(
    `/bookkeeping/clients${query ? `?${query}` : ''}`
  );
}

async function getBookkeepingClient(id: string): Promise<BookkeepingClientDetail> {
  return fetchApi<BookkeepingClientDetail>(`/bookkeeping/clients/${id}`);
}

async function getBookkeepingMonthlyProgress(month: string): Promise<MonthlyProgressResponse> {
  const params = new URLSearchParams({ month });
  return fetchApi<MonthlyProgressResponse>(`/bookkeeping/monthly-progress?${params.toString()}`);
}

async function getWorkloads(): Promise<Workload[]> {
  // /workloads was reshaped to supervisor-centric (Supervisor Workload rework). The
  // bookkeeper-centric priority list these consumers rely on (dashboard "top priority" widget +
  // the /bookkeeping workload tab) now lives at the preserved /workloads/bookkeeper-priority route.
  return fetchApi<Workload[]>('/workloads/bookkeeper-priority');
}

// ---------- React Query Hooks ----------

export function useBookkeepingDashboard() {
  return useQuery({
    queryKey: ['bookkeeping', 'dashboard'],
    queryFn: getBookkeepingDashboard,
    staleTime: 1000 * 60 * 2,
  });
}

export function useBookkeepingClients(filters?: BookkeepingClientFilters) {
  return useQuery({
    queryKey: ['bookkeeping', 'clients', filters ?? {}],
    queryFn: () => getBookkeepingClients(filters),
    staleTime: 1000 * 60 * 2,
  });
}

export function useBookkeepingClient(id: string | undefined) {
  return useQuery({
    queryKey: ['bookkeeping', 'client', id],
    queryFn: () => getBookkeepingClient(id!),
    enabled: !!id,
  });
}

export function useBookkeepingMonthlyProgress(month: string) {
  return useQuery({
    queryKey: ['bookkeeping', 'monthly-progress', month],
    queryFn: () => getBookkeepingMonthlyProgress(month),
    enabled: !!month,
    staleTime: 1000 * 60 * 2,
  });
}

export function useWorkloads() {
  return useQuery({
    queryKey: ['workloads'],
    queryFn: getWorkloads,
    staleTime: 1000 * 60 * 2,
  });
}
